import styled from '@emotion/styled';
import { useEffect } from 'react';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { mq } from '~/lib/helpers/media-query.helper';
import { useRaptorProducts } from '~/services/raptor/useRaptorProducts';
import { AspectRatio, Image, Link } from '~/shared/components';
import { CardCarousel } from '~/shared/components/CardCarousel/CardCarousel';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { useEvents } from '~/shared/hooks/useEvents';
import { _transformProductDetailsToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { GTM_LIST_NAME } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import { theme } from '~/theme';

const PreviewWrapper = styled.div(({ theme }) => ({
    position: 'relative',
    minWidth: '165px',
    padding: theme.space[4],
    background: theme.colors.brownUltraLight,
    marginLeft: theme.space[3],
    boxSizing: 'border-box',
    img: {
        mixBlendMode: 'multiply',
    },
}));

const ImageWrap = styled.div({
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    width: '100%',
});
const generateItem = (item: TProductDetails, onProductClick: () => void) => {
    return (
        <Link
            href={item.url}
            onClick={() => {
                onProductClick();
                return true;
            }}
        >
            <PreviewWrapper>
                <AspectRatio ratio={1}>
                    <ImageWrap>
                        {item.images?.[0] && (
                            <Image
                                {...item.images[0]}
                                objectFit="cover"
                                sizes={`${mq(0, 'md', false)} 50vw, 120px`}
                                layout="fill"
                            />
                        )}
                    </ImageWrap>
                </AspectRatio>
            </PreviewWrapper>
        </Link>
    );
};
const raptorAlgorithm = 'GetCookieHistory';

export const ViewedProducts = () => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { productsImpressionsEvent } = useEvents(frame);
    const {
        data: products,
        isSuccess,
        isError,
    } = useRaptorProducts({
        config: raptorAlgorithm,
    });
    const { isPlusMember } = useCustomer();
    const { productClickEvent, setList } = useEvents(frame);

    const onProductClick = (item: TProductDetails, position: number) => {
        setList(translate('pdp.viewedProducts'));
        productClickEvent(
            raptorAlgorithm,
            `${GTM_LIST_NAME.RAPTOR} - ${raptorAlgorithm}`,
            _transformProductDetailsToDataLayer(item, 1, position, isPlusMember),
        );
    };

    useEffect(() => {
        if (isSuccess && products && products.length > 0) {
            productsImpressionsEvent(
                raptorAlgorithm,
                `${GTM_LIST_NAME.RAPTOR} - ${translate('pdp.viewedProducts')}`,
                products?.map((item: any, index: any) =>
                    _transformProductDetailsToDataLayer(item, 1, index, isPlusMember),
                ),
            );
        }
    }, [isSuccess]);

    if (isError || !products?.length) {
        return null;
    }

    return (
        <ModuleContainer
            spacingTop="xs"
            spacingBottom="xs"
            backgroundColor={theme.colors.brownLight}
        >
            <CardCarousel
                options={{
                    slidesPerView: 'auto',
                }}
                headline={{ tag: 'h2', text: translate('pdp.viewedProducts'), style: 'display2' }}
                items={products?.map((p: any, index: any) =>
                    generateItem(p, () => onProductClick(p, index)),
                )}
            />
        </ModuleContainer>
    );
};
